/**
 * Hook to manage admission coordination workflow
 *
 * Provides functionality to launch the admission coordination dialog
 * for a given patient and handle visit creation upon completion.
 */

import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { useSnackbar } from '~/hooks/useSnackbar';
import {
  saveAdmissionCoordination,
  clearAdmissionCoordination,
  markAdmissionScheduled,
} from '~/services/demo/demoStateManager';
import {
  createAdmissionVisits,
  OrgConfig,
} from '~/services/demo/autoSchedulingDemo';
import {
  CARE_PATTERNS,
  DEFAULT_LOS_DAYS,
  findAdmissionVisitType,
} from '~/services/demo/mockAutoSchedulingData';

interface PatientInfo {
  id: number;
  firstName: string;
  lastName: string;
  mrn: string;
  episodeId: number;
  diagnosis?: string;
  expectedLOS?: number;
}

interface UseAdmissionCoordinationResult {
  isOpen: boolean;
  openDialog: (patient: PatientInfo) => void;
  closeDialog: () => void;
  handleComplete: (data: {
    admissionTime: string;
    providerResources: Array<{ providerId: number; providerTypeId?: number }>;
    autoScheduleToday: boolean;
    selectedVisitTypeId: number | null;
  }) => Promise<void>;
  admissionVisitType: any;
}

export function useAdmissionCoordination(): UseAdmissionCoordinationResult {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPatient, setCurrentPatient] = useState<PatientInfo | null>(null);
  const apolloClient = useApolloClient();
  const orgConfig = useOrgConfig();
  const { setErrorMessage: showSnackbar } = useSnackbar();

  const openDialog = (patient: PatientInfo) => {
    // Initialize admission coordination state
    saveAdmissionCoordination({
      needsCoordination: true,
      patientId: patient.id,
      episodeId: patient.episodeId,
      patientName: `${patient.firstName} ${patient.lastName}`,
      mrn: patient.mrn,
      admissionOrderTime: new Date().toISOString(),
      diagnosis: patient.diagnosis || 'Admission',
      expectedLOS: patient.expectedLOS || DEFAULT_LOS_DAYS,
      supplies: {
        vendor: null,
        ordered: false,
        estimatedDelivery: null,
        items: [],
      },
      transportation: {
        ordered: false,
        estimatedArrival: null,
        duration: 0,
      },
      recommendedTime: null,
      completed: false,
    });

    setCurrentPatient(patient);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setCurrentPatient(null);
  };

  const handleComplete = async (data: {
    admissionTime: string;
    providerResources: Array<{ providerId: number; providerTypeId?: number }>;
    autoScheduleToday: boolean;
    selectedVisitTypeId: number | null;
  }) => {
    if (!currentPatient) {
      showSnackbar('Patient information not found', 'error');
      return;
    }

    if (orgConfig.loading) {
      showSnackbar('Organization config is still loading', 'error');
      return;
    }

    try {
      // Get service line
      const serviceLineId = Object.values(orgConfig.serviceLines)[0]?.id;

      if (!serviceLineId) {
        throw new Error('Service line not found');
      }

      // Create admission visits
      const { admissionVisitId, todaysVisitIds } = await createAdmissionVisits(
        apolloClient,
        orgConfig as OrgConfig,
        currentPatient.id,
        currentPatient.episodeId,
        serviceLineId,
        data.admissionTime,
        data.providerResources,
        data.autoScheduleToday,
        CARE_PATTERNS.standard,
        data.selectedVisitTypeId,
        undefined, // onProgress
        currentPatient.expectedLOS || DEFAULT_LOS_DAYS
      );

      // Mark coordination as completed and flag admission as scheduled
      clearAdmissionCoordination();
      markAdmissionScheduled(currentPatient.episodeId);

      const expectedLOS = currentPatient.expectedLOS || DEFAULT_LOS_DAYS;
      const futureVisits = (expectedLOS - 1) * CARE_PATTERNS.standard.timeWindows.length;
      const totalVisits = 1 + todaysVisitIds.length + futureVisits;
      showSnackbar(
        `Episode scheduled! ${totalVisits} visits created across ${expectedLOS} days of care.`,
        'success'
      );

      // Refetch queries to update the UI
      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions', 'PatientDetailScreen'],
      });

      closeDialog();
    } catch (error: any) {
      console.error('Failed to create admission visits:', error);
      showSnackbar(`Failed to create visits: ${error.message}`, 'error');
      throw error;
    }
  };

  const admissionVisitType = !orgConfig.loading
    ? orgConfig.visitTypes[findAdmissionVisitType(orgConfig.visitTypes) || 0] || null
    : null;

  return {
    isOpen,
    openDialog,
    closeDialog,
    handleComplete,
    admissionVisitType,
  };
}
